import React, { useState } from 'react';
import { useScroll, useTransform, motion } from 'framer-motion';
import { FaIndustry, FaHandshake, FaBolt, FaNetworkWired, FaRocket, FaTools, FaCogs, FaRuler, FaCut, FaPlug, FaWrench, FaPumpSoap, FaToolbox, FaTruck, FaMicrochip, FaFlask, FaSnowflake } from 'react-icons/fa';

export default function CompanyInfo() {
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const [hoveredStrength, setHoveredStrength] = useState(null);

  const strengths = [
    { icon: <FaIndustry />, title: '包括的な製造業サポート', description: '工作機械、周辺機器、工具、工場設備など、製造業に必要な全てを提供します。' },
    { icon: <FaHandshake />, title: '信頼のネットワーク', description: '長年培った取引先との強い絆により、最適なソリューションを提案します。' },
    { icon: <FaBolt />, title: '迅速で柔軟な対応', description: '「なんでも屋」として、お客様のあらゆるニーズに素早く対応します。' },
    { icon: <FaNetworkWired />, title: '地域ネットワークの活用', description: '金属・樹脂加工の仲介など、地域の力を最大限に活用します。' },
    { icon: <FaRocket />, title: '製造業の未来を支援', description: '地域の製造業の活性化と高度化に向けて、積極的に取り組みます。' },
    { icon: <FaTools />, title: 'トータルソリューション', description: '問題解決から新技術導入まで、製造業の課題に総合的にアプローチします。' }
  ];

  const products = [
    { icon: <FaCogs />, title: '工作機械・周辺機器', description: '最新の技術を搭載した工作機械や、生産性を向上させる周辺機器を幅広く取り揃えています。', image: '/images/vrx-i700-neo-240315.png' },
    { icon: <FaRuler />, title: '切削工具・測定機具', description: '高精度な加工を実現する切削工具や、品質管理に欠かせない測定機具を提供しています。', image: '/images/sessakukougu.jpg' },
    { icon: <FaCut />, title: '特注カッター', description: 'お客様の特殊なニーズに応える特注カッターの設計・製作も承っています。', image: '/images/tokushukougu.jpg' },
    { icon: <FaPlug />, title: '伝動省力機器', description: '効率的な動力伝達と省エネルギー化を実現する最新の伝動省力機器を取り扱っています。', image: '/images/energetic.png' },
    { icon: <FaWrench />, title: '電動/エアー工具', description: '高性能な電動工具やエアー工具を取り揃え、作業効率の向上をサポートします。', image: '/images/airtool.png' },
    { icon: <FaPumpSoap />, title: '油圧/空圧機器', description: '信頼性の高い油圧機器や空圧機器を提供し、様々な産業分野での需要に応えます。', image: '/images/air_yuatu.png' },
    { icon: <FaToolbox />, title: '作業工具', description: '耐久性と使いやすさを兼ね備えた各種作業工具を取り扱い、現場作業をサポートします。', image: '/images/tool.png' },
    { icon: <FaTruck />, title: '荷役運搬機器', description: '効率的な物流を実現する荷役運搬機器を提供し、作業現場の生産性向上に貢献します。', image: '/images/unpan.png' },
    { icon: <FaMicrochip />, title: '制御機器', description: 'シーケンサなどの各種高度な自動化を可能にする最新の制御機器を取り扱い、生産ラインの効率化を支援します。', image: '/images/seigyo.png' },
    { icon: <FaFlask />, title: 'ケミカル商材', description: '各種産業用ケミカル製品を取り扱い、製造プロセスの最適化や品質及び職場環境の向上をサポートします。', image: '/images/chemical.png' },
    { icon: <FaIndustry />, title: '治具関係', description: '高精度な加工や組立を実現する各種治具を提供し、生産効率の向上に貢献します。', image: '/images/jig.png' },
    { icon: <FaSnowflake />, title: '冷暖房機器', description: '快適な作業環境を実現する高効率な冷暖房機器を取り扱い、作業効率の向上を支援します。', image: '/images/reidanbo.png' }
  ];

  return (
    <section className="py-8 md:py-16 bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500 text-white">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.h1 
          style={{ scale }}
          className="text-3xl md:text-5xl font-bold text-center mb-8 md:mb-16 relative"
        >
          共伸機工のプロフィール
          <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-24 md:w-32 h-1 bg-yellow-400"></div>
        </motion.h1>

        <motion.div
          key="company-strengths"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8 md:mb-16 relative"
        >
          <div className="absolute inset-0 bg-blue-800 transform -skew-y-3 shadow-2xl"></div>
          <div className="relative z-10 p-4 md:p-8 lg:p-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6 text-yellow-400">共伸機工について</h2>
            <div className="flex flex-col md:flex-row items-center md:items-start">
              <div className="w-full md:w-1/2 mb-4 md:mb-0 md:pr-4">
                <motion.div
                  className="relative w-full h-64 md:h-auto"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                >
                  <img
                    src="/images/honsha.png"
                    alt="共伸機工の製品イメージ"
                    className="rounded-lg shadow-2xl w-full h-full object-cover"
                  />
                </motion.div>
              </div>
              <div className="w-full md:w-1/2 md:pl-4">
                <motion.p 
                  className="text-base md:text-xl mb-4 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                >
                  共伸機工株式会社は、2006年12月12日の設立以来、金属加工業界において<span className="text-yellow-400 font-semibold">信頼と実績を積み重ねてきました</span>。私たちは、製造加工機および工具の販売を通じて、お客様の生産性向上と技術革新をサポートしています。
                </motion.p>
                <motion.p 
                  className="text-base md:text-xl leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.8 }}
                >
                  <span className="text-yellow-400 font-semibold">技術と信頼を基盤に</span>、金属加工業界の発展に貢献し続けます。お客様の課題を自分事として捉え、常に最適なソリューションを提供することで、<span className="text-yellow-400 font-semibold">産業の未来を共に創造していきます</span>。
                </motion.p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mb-8 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold text-center mb-6 md:mb-10">共伸機工の強み</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {strengths.map((strength, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-blue-800 rounded-lg shadow-xl overflow-hidden cursor-pointer"
                whileHover={{ scale: 1.05, rotate: 2 }}
                onHoverStart={() => setHoveredStrength(index)}
                onHoverEnd={() => setHoveredStrength(null)}
              >
                <div className="p-4 md:p-6">
                  <motion.div 
                    className="text-3xl md:text-5xl text-yellow-400 mb-2 md:mb-4"
                    animate={{ rotate: hoveredStrength === index ? 360 : 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {strength.icon}
                  </motion.div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">{strength.title}</h3>
                  <p className="text-sm md:text-base text-blue-100">{strength.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-3xl md:text-4xl font-semibold text-center mb-6 md:mb-10">取扱い製品</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-blue-800 rounded-lg shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/3 md:flex-shrink-0">
                    <div className="relative h-32 sm:h-48 md:h-full">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 md:p-6 md:w-2/3">
                    <div className="uppercase tracking-wide text-xs sm:text-sm md:text-base text-yellow-400 font-semibold mb-1 md:mb-2">{product.title}</div>
                    <div className="text-2xl sm:text-3xl md:text-4xl text-white mb-2 md:mb-4">{product.icon}</div>
                    <p className="text-xs sm:text-sm md:text-base text-blue-100">{product.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}